import { StepType } from '@reactour/tour';
import TourStepContent from '../TourStepContent';
import { TourConfig } from '../tourTypes';
import { blockAllNoDim, blockInteraction, sidebarMenuStep } from '../stepStyles';
import { homeTourAdvanceFromNav } from '../homeTourState';

// Step indices. OPEN_SIDEBAR / PROFILE are the optional CANtabs interaction:
// useHomeTourControl skips OPEN_SIDEBAR when the sidebar is already open (the nav
// step's Next branches straight to PROFILE) and auto-advances OPEN_SIDEBAR ->
// PROFILE once the operator opens the menu.
export const CANAPPS_HOME_STEP = {
	WELCOME: 0,
	NAV_SIDEBAR: 1,
	OPEN_SIDEBAR: 2,
	PROFILE: 3,
	PROFILE_MENU: 4,
	HOME_APPS: 5,
	BREADCRUMBS: 6,
	SEARCH: 7,
	HELP: 8,
} as const;

// A mostly "look around" tour: it describes the chrome, so steps are locked — the
// highlighted element is spotlighted but not clickable and the dimmed rest is
// blocked, so the user only uses the popover. The one exception is OPEN_SIDEBAR,
// which the operator must click to open the CANtabs menu.
const steps: StepType[] = [
	{
		// Centered welcome — no dim, but the whole page is locked (only the popover).
		selector: 'body',
		position: 'center',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				title="Welcome to CANapps 👋"
				body="Your AI-driven business management software — built to help you succeed. Let’s take a quick tour of how to get around."
			/>
		),
	},
	{
		// Spotlight the CANtabs sidebar. Anchor + re-measure the live menu the same
		// way the Add-a-user tour does, so the highlight tracks it if it expands.
		// The sidebar is full-height, so reactour's 'right' pins the box to its
		// bottom; place it to the right of the sidebar and vertically centred.
		// Next branches: if the sidebar is already open, skip the open-menu step.
		selector: '[data-tour="nav-sidebar"]',
		position: ({ right, windowHeight }, prevRect) => [
			right + 14,
			Math.max(12, windowHeight / 2 - prevRect.height / 2),
		],
		styles: blockInteraction,
		...sidebarMenuStep('[data-tour="nav-sidebar"]'),
		content: (
			<TourStepContent
				tailPosition="right"
				onNext={homeTourAdvanceFromNav}
				body="This is your navigation menu — you can use this to jump between the different apps and its sub-apps from here."
			/>
		),
	},
	{
		// OPEN_SIDEBAR — only reached when the sidebar is collapsed. The one
		// interactive step: spotlight the open arrow (no blockInteraction, so the
		// toggle stays clickable; the rest is still dimmed/blocked). The controller
		// auto-advances to PROFILE once the sidebar opens.
		selector: '[data-tour="nav-sidebar-toggle"]',
		position: 'right',
		...sidebarMenuStep('[data-tour="nav-sidebar-toggle"]'),
		content: (
			<TourStepContent
				awaitAction
				hideBack
				tailPosition="right"
				body="Your menu is collapsed — click the arrow to open it."
			/>
		),
	},
	{
		// PROFILE — spotlight the CANtabs header profile and let the operator click
		// it (interactive, no blockInteraction). The controller advances to
		// PROFILE_MENU once the dropdown opens.
		selector: '[data-tour="nav-profile"]',
		position: 'right',
		...sidebarMenuStep('[data-tour="nav-profile"]'),
		content: (
			<TourStepContent
				awaitAction
				hideBack
				tailPosition="right"
				body="This is where your profile lives — click it to see your options."
			/>
		),
	},
	{
		// PROFILE_MENU — the opened dropdown (portal). blockInteraction shows it but
		// blocks clicking the items; Next continues (the controller then closes the
		// menu so it doesn't linger behind later steps).
		selector: '[data-tour="nav-profile-menu"]',
		position: 'right',
		styles: blockInteraction,
		mutationObservables: ['[data-tour="nav-profile-menu"]'],
		resizeObservables: ['[data-tour="nav-profile-menu"]'],
		content: (
			<TourStepContent
				hideBack
				tailPosition="right"
				body="From here you can open your Profile, change Preferences, or log out."
			/>
		),
	},
	{
		selector: '[data-tour="home-apps"]',
		// The apps grid fills the full-height (scrollable) right pane, so placing
		// the box over it hides tiles. Sit it just to the LEFT of the grid,
		// vertically centred on the screen, and point the tail right at the tiles.
		position: ({ left, windowHeight }, prevRect) => [
			Math.max(12, left - prevRect.width - 16),
			Math.max(12, windowHeight / 2 - prevRect.height / 2),
		],
		styles: blockInteraction,
		content: (
			<TourStepContent
				tailPosition="left"
				hideBack
				body="These are your CANapps — each tile opens that app."
			/>
		),
	},
	{
		selector: '[data-tour="nav-breadcrumbs"]',
		position: 'bottom',
		styles: blockInteraction,
		content: (
			<TourStepContent
				tailPosition="bottom"
				body="These breadcrumbs show where you are. Use them to step back through sections."
			/>
		),
	},
	{
		selector: '[data-tour="nav-search"]',
		// Right-align under the search icon (it's near the screen edge) so the box
		// stays on-screen, and push it down; tailOffset aims the tail at the icon.
		position: ({ right, bottom }, prevRect) => [
			right - prevRect.width,
			bottom + 20,
		],
		styles: blockInteraction,
		content: (
			<TourStepContent
				tailPosition="bottom"
				tailOffset={24}
				body="Search across the whole of CANapps 4 from here — or press Ctrl/⌘ + K anywhere."
			/>
		),
	},
	{
		selector: '[data-tour="nav-help"]',
		// Right-align under the help button (far-right) so the box stays on-screen.
		position: ({ right, bottom }, prevRect) => [
			right - prevRect.width,
			bottom + 20,
		],
		styles: blockInteraction,
		content: (
			<TourStepContent
				tailPosition="bottom"
				tailOffset={24}
				body="Find guided tours and help anytime from this menu. That’s the tour — you’re all set!"
			/>
		),
	},
];

export const canappsNavigationTour: TourConfig = {
	steps,
	startRoute: '/',
	allowedRoutes: ['/'],
};
